import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const ArrayInput = ({ label, items, setItems, name }) => (
  <div className="space-y-3">
    <label className="block text-sm font-bold text-text-secondary">{label}</label>
    {items.map((item, idx) => (
      <div key={idx} className="flex items-center gap-2">
        <input
          type="text"
          value={item}
          onChange={(e) => {
            const newItems = [...items];
            newItems[idx] = e.target.value;
            setItems(newItems);
          }}
          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-text-primary focus:border-accent"
        />
        <button
          type="button"
          onClick={() => setItems(items.filter((_, i) => i !== idx))}
          className="w-10 h-10 rounded-xl bg-red-500/10 hover:bg-red-500/20 flex flex-shrink-0 items-center justify-center text-red-400 transition-smooth border border-red-500/10"
        >
          <Icon name="Trash2" size={16} />
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => setItems([...items, ''])}
      className="text-sm font-semibold text-accent hover:text-accent-light flex items-center gap-1 mt-2"
    >
      <Icon name="Plus" size={14} /> Add Item
    </button>
    <input type="hidden" name={name} value={JSON.stringify(items.filter(i => typeof i === 'string' ? i.trim() !== '' : true))} />
  </div>
);

const ToolArrayInput = ({ label, items, setItems, name }) => (
  <div className="space-y-3">
    <label className="block text-sm font-bold text-text-secondary">{label}</label>
    {items.map((item, idx) => (
      <div key={idx} className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Tool Name (e.g. Git)"
          value={item.name || ''}
          onChange={(e) => {
            const newItems = [...items];
            newItems[idx] = { ...newItems[idx], name: e.target.value };
            setItems(newItems);
          }}
          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-text-primary focus:border-accent"
        />
        <input
          type="text"
          placeholder="Icon (e.g. GitBranch)"
          value={item.icon || ''}
          onChange={(e) => {
            const newItems = [...items];
            newItems[idx] = { ...newItems[idx], icon: e.target.value };
            setItems(newItems);
          }}
          className="w-48 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-text-primary focus:border-accent"
        />
        <button
          type="button"
          onClick={() => setItems(items.filter((_, i) => i !== idx))}
          className="w-10 h-10 rounded-xl bg-red-500/10 hover:bg-red-500/20 flex flex-shrink-0 items-center justify-center text-red-400 transition-smooth border border-red-500/10"
        >
          <Icon name="Trash2" size={16} />
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => setItems([...items, { name: '', icon: '' }])}
      className="text-sm font-semibold text-accent hover:text-accent-light flex items-center gap-1 mt-2"
    >
      <Icon name="Plus" size={14} /> Add Tool
    </button>
    <input type="hidden" name={name} value={JSON.stringify(items.filter(i => i.name && i.name.trim() !== ''))} />
  </div>
);

const ContentManager = ({ type, adminKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [formSkills, setFormSkills] = useState([]);
  const [formToolsUsed, setFormToolsUsed] = useState([]);
  const [formIndustry, setFormIndustry] = useState([]);
  const [formServices, setFormServices] = useState([]);
  const [formTeam, setFormTeam] = useState([]);
  const [formResults, setFormResults] = useState([]);
  const [isTool, setIsTool] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [type]);

  const fetchItems = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/${type}`, { 
      cache: 'no-store',
      headers: { 'X-Admin-Key': adminKey }
    })
      .then(res => res.json())
      .then(data => {
        setItems(data);
        setLoading(false);
      });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    // Convert types
    if (data.id) data.id = parseInt(data.id);
    if (data.display_order) data.display_order = parseInt(data.display_order);
    data.is_featured = data.is_featured === 'on' ? 1 : 0;

    try {
      const res = await fetch(`${API_BASE_URL}/api/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Saved successfully!' });
        setTimeout(() => setEditingItem(null), 1000);
        fetchItems();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Save failed');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/${type}/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.ok) fetchItems();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) return <div className="h-64 bg-white/5 rounded-2xl animate-pulse"></div>;

  return (
    <div className="space-y-6 pb-12">
      <div className="flex justify-end">
        <button 
          onClick={() => {
            setEditingItem({});
            setFormSkills([]);
            setFormToolsUsed([]);
            setFormIndustry([]);
            setFormServices([]);
            setFormTeam([]);
            setFormResults([]);
            setIsTool(false);
            setMessage(null);
          }}
          className="bg-accent/10 hover:bg-accent/20 text-accent px-4 py-2 rounded-xl border border-accent/20 transition-smooth flex items-center gap-2"
        >
          <Icon name="Plus" size={20} />
          Add New {type === 'skills' ? 'Skill' : 'Achievement'}
        </button>
      </div>

      <div className="grid gap-4">
        {items.map((item) => (
          <div key={item.id} className="bg-surface/50 border border-white/10 p-4 rounded-xl flex items-center justify-between group">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name={item.icon} size={20} className="text-accent" />
              </div>
              <div>
                <h4 className="font-semibold text-text-primary">
                  {type === 'skills' ? item.category : item.title}
                </h4>
                <p className="text-sm text-text-secondary">
                  {type === 'skills' ? (() => {
                    if (item.category === 'Tools') return 'Collection of Tools & Technologies';
                    try {
                      const arr = JSON.parse(item.name || '[]');
                      return Array.isArray(arr) ? arr.join(', ').substring(0, 60) + '...' : item.name;
                    } catch (e) {
                      return item.name;
                    }
                  })() : item.description?.substring(0, 60) + '...'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => {
                  setEditingItem(item);
                  if (type === 'skills') {
                    const isTools = item.category === 'Tools';
                    setIsTool(isTools);
                    try {
                      const parsed = JSON.parse(item.name || '[]');
                      if (Array.isArray(parsed)) {
                        if (isTools) {
                           setFormSkills(parsed.map(p => typeof p === 'string' ? { name: p, icon: 'Code' } : p));
                        } else {
                           setFormSkills(parsed);
                        }
                      } else {
                        setFormSkills(isTools ? [{ name: item.name, icon: 'Code' }] : [item.name]);
                      }
                    } catch (e) {
                      setFormSkills(isTools ? [{ name: item.name, icon: 'Code' }] : [item.name]);
                    }
                  } else if (type === 'projects') {
                    const parseArray = (val) => {
                      if (!val) return [];
                      if (Array.isArray(val)) return val;
                      try { return JSON.parse(val); } catch(e) { return [val]; }
                    };
                    setFormToolsUsed(parseArray(item.toolsUsed));
                    setFormIndustry(parseArray(item.industry));
                    setFormServices(parseArray(item.services));
                    setFormTeam(parseArray(item.team));
                    setFormResults(parseArray(item.results));
                  }
                  setMessage(null);
                }}
                className="p-2 text-text-secondary hover:text-accent transition-smooth"
              >
                <Icon name="Edit" size={18} />
              </button>
              <button 
                onClick={() => handleDelete(item.id)}
                className="p-2 text-text-secondary hover:text-red-500 transition-smooth"
              >
                <Icon name="Trash2" size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editingItem && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-white/10 rounded-3xl p-8 max-w-xl w-full shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">{editingItem.id ? 'Edit' : 'Add'} {type}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              {editingItem.id && <input type="hidden" name="id" value={editingItem.id} />}
              
              {type === 'skills' ? (
                <>
                  <div className="flex gap-4 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="skillType" 
                        checked={!isTool} 
                        onChange={() => setIsTool(false)}
                        className="accent-accent w-4 h-4"
                      />
                      <span className="text-text-primary text-sm font-medium">Standard Category</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="skillType" 
                        checked={isTool} 
                        onChange={() => setIsTool(true)}
                        className="accent-accent w-4 h-4"
                      />
                      <span className="text-text-primary text-sm font-medium">Tools & Technologies</span>
                    </label>
                  </div>
                  
                  {isTool ? (
                    <>
                      <input type="hidden" name="category" value="Tools" />
                      <ToolArrayInput label="List of Tools" items={formSkills} setItems={setFormSkills} name="name" />
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm mb-1 text-text-secondary">Category Name</label>
                        <input name="category" defaultValue={editingItem.category !== 'Tools' ? editingItem.category : ''} required={!isTool} placeholder="e.g. Data Science, Remote Sensing" className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                      </div>
                      <ArrayInput label="Skill Sets" items={formSkills} setItems={setFormSkills} name="name" />
                    </>
                  )}
                </>
              ) : type === 'projects' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Project Title</label>
                      <input name="title" defaultValue={editingItem.title} required className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Project Type</label>
                      <input name="type" defaultValue={editingItem.type} required placeholder="e.g. Full-Stack Dev" className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Short Description</label>
                      <textarea name="description" defaultValue={editingItem.description} required className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent h-24" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Detailed Content (HTML Supported)</label>
                      <textarea name="detailedContent" defaultValue={editingItem.detailedContent} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent h-48 font-mono text-sm" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 pt-4 border-t border-white/10">
                    <div className="space-y-6">
                      <ArrayInput label="Tools Used" items={formToolsUsed} setItems={setFormToolsUsed} name="toolsUsed" />
                      <ArrayInput label="Services Provided" items={formServices} setItems={setFormServices} name="services" />
                      <ArrayInput label="Key Results" items={formResults} setItems={setFormResults} name="results" />
                    </div>
                    <div className="space-y-6">
                      <ArrayInput label="Industries" items={formIndustry} setItems={setFormIndustry} name="industry" />
                      <ArrayInput label="Team Members" items={formTeam} setItems={setFormTeam} name="team" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Image URL</label>
                      <input name="image" defaultValue={editingItem.image} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Client</label>
                      <input name="client" defaultValue={editingItem.client} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Live URL</label>
                      <input name="liveUrl" defaultValue={editingItem.liveUrl} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Code URL</label>
                      <input name="codeUrl" defaultValue={editingItem.codeUrl} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Completion Date</label>
                      <input name="completionDate" defaultValue={editingItem.completionDate} type="date" className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Duration</label>
                      <input name="duration" defaultValue={editingItem.duration} placeholder="e.g. 3 Months" className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Author</label>
                      <input name="author" defaultValue={editingItem.author || 'Sudhan R'} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Main Metric (e.g. +140% Growth)</label>
                      <input name="metric" defaultValue={editingItem.metric} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm mb-1 text-text-secondary">Title</label>
                    <input name="title" defaultValue={editingItem.title} required className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Description</label>
                      <textarea name="description" defaultValue={editingItem.description} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent h-24" />
                    </div>
                    <div>
                      <label className="block text-sm mb-1 text-text-secondary">Metric / Result (Optional)</label>
                      <input name="metric" defaultValue={editingItem.metric} placeholder="e.g. 500+ Imparted or 4.5/5 Rating" className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1 text-text-secondary">Icon Name (Lucide)</label>
                  <input name="icon" defaultValue={editingItem.icon} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm mb-1 text-text-secondary">Display Order</label>
                    <input name="display_order" type="number" defaultValue={editingItem.display_order || 0} className="w-full bg-background border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-accent" />
                  </div>
                  {type === 'skills' && (
                    <div className="flex items-center gap-2 pt-6">
                      <input type="checkbox" name="is_featured" id="is_featured" defaultChecked={!!editingItem.is_featured} className="w-4 h-4 bg-background border-white/10 rounded accent-accent" />
                      <label htmlFor="is_featured" className="text-sm text-text-secondary">Featured</label>
                    </div>
                  )}
                </div>
              </div>

              {message && (
                <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
                  message.type === 'success' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
                }`}>
                  <Icon name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
                  <span>{message.text}</span>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button type="button" onClick={() => { setEditingItem(null); setMessage(null); }} className="px-6 py-2 rounded-lg text-text-secondary hover:bg-white/5 transition-smooth">Cancel</button>
                <button type="submit" disabled={saving} className="bg-accent text-white px-8 py-2 rounded-lg font-semibold hover:bg-accent/90 transition-smooth disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManager;
